import { useState, useEffect, useMemo, useRef } from 'react';
import { Check, Plus, Minus, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';
import { useProductModifiers, SelectedModifier, ModifierGroup, ModifierOption } from '@/hooks/useModifiers';

interface Product {
  id: string;
  name: string;
  price: number;
  effective_price?: number;
  image_url?: string | null;
}

interface ModifierSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
  onConfirm: (product: Product, modifiers: SelectedModifier[], quantity: number) => void;
  initialModifiers?: SelectedModifier[];
  initialQuantity?: number;
}

export function ModifierSelectionModal({
  open,
  onOpenChange,
  product,
  onConfirm,
  initialModifiers = [],
  initialQuantity = 1,
}: ModifierSelectionModalProps) {
  const { formatCurrency } = useCurrency();
  const { productModifiers, isLoading, validateSelections, getDefaultSelections } = useProductModifiers(product?.id ?? null);

  const [selectedModifiers, setSelectedModifiers] = useState<SelectedModifier[]>(initialModifiers);
  const [quantity, setQuantity] = useState(initialQuantity);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  
  // Track if we've initialized for this modal open session
  const hasInitializedRef = useRef(false);
  const lastProductIdRef = useRef<string | null>(null);

  // Reset state only when modal opens with a new product
  useEffect(() => {
    if (open && product) {
      // Only reset if this is a new product or we haven't initialized yet
      if (lastProductIdRef.current !== product.id || !hasInitializedRef.current) {
        if (initialModifiers.length > 0) {
          setSelectedModifiers(initialModifiers);
        } else {
          // Apply default selections
          const defaults = getDefaultSelections();
          setSelectedModifiers(defaults);
        }
        setQuantity(initialQuantity);
        setValidationErrors([]);
        lastProductIdRef.current = product.id;
        hasInitializedRef.current = true;
      }
    } else if (!open) {
      // Reset the initialization flag when modal closes
      hasInitializedRef.current = false;
    }
  }, [open, product?.id]);
  
  // Only apply defaults once when productModifiers load (if not already initialized from props)
  useEffect(() => {
    if (open && product && hasInitializedRef.current && selectedModifiers.length === 0 && productModifiers.length > 0) {
      const defaults = getDefaultSelections();
      if (defaults.length > 0) {
        setSelectedModifiers(defaults);
      }
    }
  }, [productModifiers.length]);

  // Calculate total price including modifiers
  const modifierTotal = useMemo(() => {
    return selectedModifiers.reduce((sum, mod) => sum + mod.price, 0);
  }, [selectedModifiers]);

  const itemPrice = (product?.price ?? 0) + modifierTotal;
  const totalPrice = itemPrice * quantity;

  // Check if an option is selected
  const isOptionSelected = (optionId: string): boolean => {
    return selectedModifiers.some(mod => mod.id === optionId);
  };

  // Get count of selections for a group
  const getGroupSelectionCount = (groupId: string): number => {
    return selectedModifiers.filter(mod => mod.group_id === groupId).length;
  };

  // Handle option toggle
  const handleOptionToggle = (group: ModifierGroup, option: ModifierOption) => {
    const isSelected = isOptionSelected(option.id);
    const currentGroupCount = getGroupSelectionCount(group.id);
    const maxSelections = Number(group.max_selections ?? Infinity);

    if (isSelected) {
      // Deselect
      setSelectedModifiers(prev => prev.filter(mod => mod.id !== option.id));
    } else {
      // Check if we can add more to this group
      if (currentGroupCount >= maxSelections) {
        if (maxSelections === 1) {
          // Single selection - replace existing
          setSelectedModifiers(prev => [
            ...prev.filter(mod => mod.group_id !== group.id),
            {
              id: option.id,
              name: option.name,
              price: option.price_adjustment ?? 0,
              group_id: group.id,
              group_name: group.name,
            },
          ]);
        }
        // If max > 1 and at limit, do nothing (user must deselect first)
        return;
      }

      // Add selection
      setSelectedModifiers(prev => [
        ...prev,
        {
          id: option.id,
          name: option.name,
          price: option.price_adjustment ?? 0,
          group_id: group.id,
          group_name: group.name,
        },
      ]);
    }

    // Clear validation errors when user makes changes
    setValidationErrors([]);
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!product) return;

    const { valid, errors } = validateSelections(selectedModifiers);
    if (!valid) {
      setValidationErrors(errors);
      return;
    }

    onConfirm(product, selectedModifiers, quantity);
    onOpenChange(false);
  };

  // Get selection requirement label
  const getSelectionLabel = (group: ModifierGroup): string => {
    const min = group.min_selections ?? 0;
    const max = group.max_selections ?? Infinity;

    if (group.is_required && max === 1) return 'Required - Choose 1';
    if (group.is_required && min === max) return `Required - Choose ${min}`;
    if (group.is_required) return `Required - Choose ${min}-${max}`;
    if (max === 1) return 'Optional - Choose up to 1';
    if (max < Infinity) return `Optional - Choose up to ${max}`;
    return 'Optional';
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-start gap-4">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.name}
                className="w-16 h-16 rounded-lg object-cover border"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center border">
                <span className="text-2xl font-bold text-muted-foreground">
                  {product.name.charAt(0)}
                </span>
              </div>
            )}
            <div className="flex-1">
              <DialogTitle className="text-lg">{product.name}</DialogTitle>
              <DialogDescription className="mt-1">
                Customize your order
              </DialogDescription>
              <p className="text-lg font-semibold text-primary mt-1">
                {formatCurrency(product.effective_price ?? product.price)}
                {modifierTotal > 0 && (
                  <span className="text-sm text-muted-foreground ml-2">
                    +{formatCurrency(modifierTotal)} modifiers
                  </span>
                )}
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <div className="text-sm text-destructive">
              {validationErrors.map((error, i) => (
                <p key={i}>{error}</p>
              ))}
            </div>
          </div>
        )}

        {/* Modifier Groups */}
        <div className="flex-1 overflow-y-auto space-y-4 py-2 -mx-6 px-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            productModifiers.map(pmg => {
              const group = pmg.modifier_group;
              if (!group) return null;

              const selectionCount = getGroupSelectionCount(group.id);
              const isGroupValid = !group.is_required || selectionCount >= (group.min_selections ?? 1);

              return (
                <div key={group.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-sm">{group.name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {getSelectionLabel(group)}
                      </p>
                    </div>
                    {group.is_required && !isGroupValid && (
                      <Badge variant="destructive" className="text-xs">
                        Required
                      </Badge>
                    )}
                    {selectionCount > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {selectionCount} selected
                      </Badge>
                    )}
                  </div>

                  <div className="grid gap-2">
                    {group.options?.map(option => {
                      const isSelected = isOptionSelected(option.id);
                      const maxSelections = Number(group.max_selections ?? Infinity);
                      const atLimit = selectionCount >= maxSelections && !isSelected;

                      return (
                        <button
                          type="button"
                          key={option.id}
                          onClick={() => handleOptionToggle(group, option)}
                          disabled={atLimit && maxSelections > 1}
                          className={cn(
                            "flex items-center justify-between p-3 rounded-lg border transition-all text-left",
                            isSelected
                              ? "border-primary bg-primary/5 ring-1 ring-primary"
                              : "border-border hover:border-primary/50 hover:bg-accent/50",
                            atLimit && maxSelections > 1 && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={cn(
                                "w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                                isSelected
                                  ? "border-primary bg-primary"
                                  : "border-muted-foreground"
                              )}
                            >
                              {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                            </div>
                            <span className="font-medium text-sm">{option.name}</span>
                          </div>
                          {(option.price_adjustment ?? 0) !== 0 && (
                            <span
                              className={cn(
                                "text-sm font-medium",
                                (option.price_adjustment ?? 0) > 0
                                  ? "text-amber-600"
                                  : "text-green-600"
                              )}
                            >
                              {(option.price_adjustment ?? 0) > 0 ? '+' : ''}
                              {formatCurrency(option.price_adjustment ?? 0)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Quantity Selector */}
        <div className="flex items-center justify-between py-3 border-t">
          <span className="text-sm font-medium">Quantity</span>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setQuantity(q => Math.max(1, q - 1))}
              disabled={quantity <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-8 text-center font-semibold">{quantity}</span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setQuantity(q => q + 1)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-xl font-bold">{formatCurrency(totalPrice)}</p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={handleConfirm}>
                Add to Order
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
